import React, { useCallback } from 'react';
import { Select } from 'antd';
import { TableTransfer } from '../tableTransfer/TableTransfer';
import './styles.less';

const SelectOrTransfer = ( { value, dataSource, onChange, mode = 'select', selectAll = false,
    showSearch = true, searchPlaceholder = 'Search...', customSearchFilter, ...otherProps } ) => {

    const handleSearchFilter = ( input, option ) => {
        if ( customSearchFilter ) {
            return customSearchFilter( input, option );
        }
        return option.label.toLocaleLowerCase().includes( input.toLocaleLowerCase() );
    };

    /**
     * Handles the onChange event triggered by the Transfer component.
     * 
     * @param { string[] } selectedValues - All selected values.
     * @param { string } direction - The direction of the last transfer.
     * @param { string[] } transferedValues - The values of the last transfered options.
     */
    const handleTransferOnChange = useCallback( ( selectedValues, direction, transferedValues ) => {
        if ( onChange ) {
            onChange( selectedValues );
        }
    }, [ onChange ] );

    /**
     * Handles the onChange event triggered by the Select component.
     * 
     * @param { string[] | string } selectedValue - The selected value. This will be an array of values of multiple options can be selected.
     * @param { object[] | object } selectedOption - The selected option. This will be an array of options of multiple options can be selected.
     */
    const handleSelectOnChange = useCallback( ( selectedValues, selectedOptions ) => {
        if ( onChange ) {
            onChange( selectedValues );
        }
    }, [ onChange ] );

    /**
     * The transfer columns to render for each language option. Note that this is only
     * used if the selector mode is `'transfer'`.
     */
    const DEFAULT_TRANSFER_COLUMNS = [
        {
            dataIndex: 'label',
            title: 'Name',
            render: ( label, data ) => {
                return label;
            }
        }
    ];

    switch ( mode ) {
        case 'transfer': {
            return (
                <TableTransfer
                    targetKeys={ value }
                    dataSource={ dataSource }
                    titles={ [ 'Available', 'Selected' ] }
                    onChange={ handleTransferOnChange }
                    sourceColumns={ DEFAULT_TRANSFER_COLUMNS }
                    targetColumns={ DEFAULT_TRANSFER_COLUMNS }
                    locale={ { searchPlaceholder: searchPlaceholder } }
                    showSearch={ showSearch }
                    selectAll={ selectAll }
                    filterOption={ handleSearchFilter }
                    listStyle={ {
                        width: '100%',
                        height: 350,
                    } }
                    { ...otherProps }
                />
            );
        }

        case 'select':
        case 'select-single':
        case 'select-multiple':
        default: {
            return (
                <Select
                    value={ value }
                    onChange={ handleSelectOnChange }
                    mode={ ( mode !== 'select-single' ) ? 'multiple' : undefined }
                    showArrow
                    optionLabelProp="label"
                    optionFilterProp="label"
                    placeholder={ showSearch ? searchPlaceholder : undefined }
                    showSearch={ showSearch }
                    filterOption={ handleSearchFilter }
                    { ...otherProps }
                >
                    { /* Options rendered manually instead of `options` prop because of issue with `onChange` prop */ }
                    { ( dataSource || [] ).map( ( option, index ) => (
                        <Select.Option
                            key={ `select-option-${ index }` }
                            { ...option }
                            value={ option?.key }
                        >
                            { option.display ?? option?.label }
                        </Select.Option>
                    ) ) }
                </Select>
            );
        }
    }
};

export default SelectOrTransfer;
